"use client";

import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const MIN_CHARS = 10;

// Modale de signalement partagée (leçons Reps + quiz interactifs) pour une UX
// identique. Le parent fournit les motifs, le prompt, le placeholder et un
// onSubmit(reasonId, customMessage) qui réalise l'appel API (la modale gère
// seulement l'UI : liste des motifs, champ libre, états envoi/erreur/succès).
//
// reasons : [{ id, label, custom }] — custom: true ouvre le champ texte libre.
export default function ReportModal({
    open,
    onClose,
    reasons = [],
    prompt,
    placeholder,
    onSubmit,
}) {
    const t = useTranslations();
    const [view, setView] = useState("list"); // "list" | "other" | "done"
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(false);
    const [done, setDone] = useState(false);

    // Repart d'un état propre à chaque ouverture (le parent peut fermer la modale
    // en passant simplement open=false, sans appeler handleClose).
    useEffect(() => {
        if (open) {
            setView("list");
            setText("");
            setSending(false);
            setError(false);
            setDone(false);
        }
    }, [open]);

    function reset() {
        setView("list");
        setText("");
        setSending(false);
        setError(false);
        setDone(false);
    }

    function handleClose() {
        reset();
        onClose();
    }

    async function submit(reasonId, customMessage) {
        if (sending) return;
        setSending(true);
        setError(false);
        try {
            await onSubmit(reasonId, customMessage);
            setDone(true);
            setTimeout(() => handleClose(), 1700);
        } catch (e) {
            setError(true);
        } finally {
            setSending(false);
        }
    }

    const remaining = MIN_CHARS - text.trim().length;

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={() => !done && handleClose()}
                    className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/40 p-0 backdrop-blur-[6px] sm:items-center sm:p-6"
                >
                    <motion.div
                        initial={{ y: "100%", opacity: 0.6 }}
                        animate={{ y: 0, opacity: 1 }}
                        exit={{ y: "100%", opacity: 0 }}
                        transition={{
                            type: "spring",
                            damping: 32,
                            stiffness: 380,
                            mass: 0.75,
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full overflow-hidden rounded-t-[28px] bg-white shadow-2xl dark:bg-[#1E2C3A] sm:mx-4 sm:max-w-md sm:rounded-3xl"
                    >
                        <div className="flex justify-center pb-0 pt-3 sm:hidden">
                            <div className="h-[3px] w-9 rounded-full bg-gray-200 dark:bg-white/15" />
                        </div>

                        {done ? (
                            <div className="px-6 py-10 text-center">
                                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
                                    <SolarIcon
                                        name="check-circle-bold"
                                        className="text-2xl"
                                    />
                                </div>
                                <h3 className="mt-3 text-[17px] font-bold text-gray-900 dark:text-white">
                                    {t("report_thanks_title")}
                                </h3>
                                <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                                    {t("report_thanks_msg")}
                                </p>
                            </div>
                        ) : view === "other" ? (
                            <>
                                <div className="px-6 pb-1 pt-6">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setView("list");
                                            setText("");
                                            setError(false);
                                        }}
                                        className="-ml-1 mb-2 flex items-center gap-1 rounded-lg px-1 py-0.5 text-xs font-semibold text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-grayDark-200"
                                    >
                                        <SolarIcon name="alt-arrow-left-linear" />
                                        {t("report_back")}
                                    </button>
                                    <h3 className="text-[18px] font-bold tracking-tight text-gray-900 dark:text-white">
                                        {t("report_custom_title")}
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                                        {t("report_custom_prompt")}
                                    </p>
                                </div>
                                <div className="px-6 pb-2 pt-3">
                                    <textarea
                                        value={text}
                                        onChange={(e) => setText(e.target.value)}
                                        rows={4}
                                        maxLength={1000}
                                        autoFocus
                                        placeholder={placeholder}
                                        className="w-full resize-none rounded-2xl border border-gray-200 bg-white px-4 py-3 text-sm text-gray-800 outline-hidden transition-colors placeholder:text-gray-400 focus:border-gray-300 dark:border-white/15 dark:bg-white/[0.04] dark:text-grayDark-100 dark:focus:border-white/25"
                                    />
                                    <div className="mt-1.5 px-1 text-xs">
                                        {remaining > 0 ? (
                                            <span className="text-gray-400">
                                                {t("report_char_remaining", {
                                                    count: remaining,
                                                })}
                                            </span>
                                        ) : (
                                            <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                                {t("report_ready")}
                                            </span>
                                        )}
                                    </div>
                                    {error && (
                                        <p className="mt-2 text-xs font-medium text-red-500">
                                            {t("report_send_error")}
                                        </p>
                                    )}
                                </div>
                                <div
                                    className="px-6 pt-1"
                                    style={{
                                        paddingBottom:
                                            "max(1.25rem, env(safe-area-inset-bottom))",
                                    }}
                                >
                                    <button
                                        type="button"
                                        disabled={remaining > 0 || sending}
                                        onClick={() => {
                                            const other = reasons.find(
                                                (r) => r.custom,
                                            );
                                            submit(
                                                other ? other.id : "other",
                                                text.trim(),
                                            );
                                        }}
                                        className="w-full rounded-2xl bg-orange-500 py-3 text-sm font-bold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400 dark:disabled:bg-white/10 dark:disabled:text-grayDark-500"
                                    >
                                        {sending
                                            ? t("report_sending")
                                            : t("report_submit")}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <>
                                <div className="px-6 pb-1 pt-6">
                                    <h3 className="text-[18px] font-bold tracking-tight text-gray-900 dark:text-white">
                                        {t("report_modal_title")}
                                    </h3>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-grayDark-400">
                                        {prompt}
                                    </p>
                                </div>
                                <div className="flex flex-col gap-2 px-6 pb-2 pt-3">
                                    {reasons.map((r) => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            disabled={sending}
                                            onClick={() =>
                                                r.custom
                                                    ? setView("other")
                                                    : submit(r.id, null)
                                            }
                                            className="flex items-center justify-between gap-3 rounded-2xl border border-gray-200 px-4 py-3 text-left text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-white/15 dark:text-grayDark-200 dark:hover:bg-white/[0.05]"
                                        >
                                            {r.label}
                                            <SolarIcon
                                                name="alt-arrow-right-linear"
                                                className="shrink-0 text-gray-400"
                                            />
                                        </button>
                                    ))}
                                </div>
                                {error && (
                                    <p className="px-6 pb-1 text-xs font-medium text-red-500">
                                        {t("report_send_error")}
                                    </p>
                                )}
                                <div
                                    className="px-6 pt-1"
                                    style={{
                                        paddingBottom:
                                            "max(1.25rem, env(safe-area-inset-bottom))",
                                    }}
                                >
                                    <button
                                        type="button"
                                        onClick={handleClose}
                                        className="w-full rounded-2xl py-3 text-sm font-semibold text-gray-500 transition-colors hover:bg-gray-50 dark:text-grayDark-400 dark:hover:bg-white/[0.05]"
                                    >
                                        {t("report_cancel")}
                                    </button>
                                </div>
                            </>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
